// Session 28: the "how it works" content, one entry per service.
//
// The machinery for this already existed and was unused: a
// portal_explainer_dismissals table, a portal_dismiss_explainer RPC, and
// explainers_dismissed on v_portal_me. All that was missing was the words and
// somewhere to put them.
//
// Written from Leena's own dictation (Agency Brain > 03 Apps > Portal and
// Workspace Backlog 2026-10-09), kept close to her phrasing rather than
// rewritten, because the tone is the point.
//
// Rules these follow:
//   - second person, warm and direct, no system words
//   - no em dashes
//   - every service says what the client has to DO, not what we built
//   - never shown to an agency user

export const EXPLAINERS = {

  /* -------------------------------------------------- the first-run panel */
  portal_overview: {
    title: 'Welcome. Here is what this is.',
    intro: 'This is where everything we do for you lives. You will only see the parts you are signed up for, so if something below is missing from your menu, it is simply not part of your plan yet.',
    steps: [
      'Blogs. We plan them three months ahead, ask you a few questions, write them, and you approve them before they go anywhere.',
      'SEO recommendations. A list of fixes we found on your website, in plain language, that you work through.',
      'Google Business Profile. A review of your listing with a step by step fix list.',
      'AI courses. Practical training for you and your team.',
      'Results. Your website traffic and your Google search performance, already connected and updating.',
    ],
    outro: 'Everything here can be reopened later from the question mark next to any section heading.',
  },

  /* ------------------------------------------------------------- blogs -- */
  blogs: {
    title: 'How your blogs work',
    intro: 'The short version: we plan, you answer a few questions, we write, you approve, it publishes.',
    steps: [
      'We start with a blog strategy for your next three months. Review it and approve it. Until you approve the plan, we cannot start writing.',
      'You can remove future topics later. If a topic stops making sense by the time we reach it, take it off.',
      'Each week you get the next blog we will be writing, along with a few key questions. What you know about your business is what makes a blog worth reading, which is why we ask.',
      'You do not need to type a lot. Open the questions here and record your answers out loud. Do not worry about phrasing, we just want the ideas.',
      'Then we write it and email it to you. You can always read it here instead.',
      'You add the images. We need a hero image at minimum and we would like a few more through the post. You can decline the extras.',
      'Once you approve it, it is queued for its publish date.',
    ],
    outro: 'These blogs are written to help you rank, so read them with that in mind. We capture how you sound as well as we can, but things like the length and the questions at the end are there deliberately, because they are what gets you found.',
  },

  /* -------------------------------------------- blogs, auto-approve note - */
  blogs_auto: {
    title: 'How your blogs work',
    intro: 'The short version: we plan, you answer a few questions, we write, and it publishes unless you tell us otherwise.',
    steps: [
      'We start with a blog strategy for your next three months. It arrives in your email and lives here too.',
      'There is a deadline for your feedback. If we do not hear from you by then, we take it as approved and carry on. We will remind you first.',
      'Each week you get the next blog we will be writing, along with a few key questions. Record your answers here rather than typing them, it is faster and you will say more.',
      'We write it, and we create the images for you. If you do not like one, say so and we will change it.',
      'Once it is approved, it is queued for its publish date.',
    ],
    outro: 'You can step in at any point. Nothing stops you opening a blog and changing it before it goes.',
  },

  /* --------------------------------------------- SEO recommendations ---- */
  seo_recommendations: {
    title: 'How your SEO list works',
    intro: 'We looked at every page on your website and found the things holding it back. They are grouped by problem, in plain language, so you can work through them rather than read a report.',
    steps: [
      'Open a group to see which pages it affects and exactly what to change.',
      'Make the change on your website, then mark that page off here. Marking it off is what keeps your progress honest.',
      'Work at your own pace. Nothing expires.',
      'Every quarter we run the whole check again, so you come back to a fresh list with whatever is new.',
    ],
    outro: 'Where a fix is one we can make for you, you will see that on the item. Everything else is yours to make, and the instructions are written so you can hand them to whoever looks after your site.',
  },

  /* ------------------------------------------------------- GMB audit ---- */
  gmb_audit: {
    title: 'How your Google Business Profile review works',
    intro: 'Your Google Business Profile is what people see before they ever reach your website. We review it and give you a fix list.',
    steps: [
      'Read through the review. Each item says what is wrong and what good looks like.',
      'Make the change in your Google Business Profile.',
      'Mark the item off here so you can see what is left.',
    ],
    outro: 'Most of these are quick. The ones that take longer, like getting reviews and keeping photos current, are the ones that move the needle most.',
  },

  /* ---------------------------------------------------------- courses --- */
  ai_courses: {
    title: 'How the courses work',
    intro: 'Practical training you work through at your own pace, on your own real work rather than made-up examples.',
    steps: [
      'Each lesson takes fifteen to twenty minutes. Read it, then do the one exercise.',
      'Every lesson ends with a prompt card you can save. Those build into your own prompt library, which stays yours after the course.',
      'The quick checks at the end of a lesson are optional. Skipping them holds nothing back.',
      'Everyone at your company who signs in gets the course, and progress is tracked per person.',
    ],
    outro: 'If something you wanted help with is not covered, there is a box at the end of every lesson. We read those and they decide what we build next.',
  },

  /* -------------------------------------------------------- analytics --- */
  analytics: {
    title: 'What you are looking at',
    intro: 'We connected your Google Analytics and your Google Search Console when you came on board, so this updates on its own. You do not have to do anything to keep it current.',
    steps: [
      'Visitors and sessions come from Google Analytics: how many people reached your site and where they came from.',
      'Clicks, impressions and position come from Google Search Console: how often you appeared in Google and how often someone chose you.',
      'Position is an average across every search you showed up for, so it moves slowly. That is normal.',
    ],
    outro: 'SEO is a slow instrument. Look at the shape over months rather than the number this week.',
  },
};

export function explainerFor(id) {
  return EXPLAINERS[id] || null;
}
